import React, { useState } from 'react';

// Add / edit an employee, per the Locations redesign.
//
// An employee is a named holder of devices, not a Bench login — adding someone
// here grants no access to anything. That distinction matters enough to say on
// the screen, because "add employee" reads like user management otherwise.
//
// Only the name is required. Until this form existed the stored record was
// {id, name} and nothing else, which is why the People tab showed "No role" and
// an empty contact line for everyone; the remaining five fields are what fill
// those in, and they stay optional so an existing record is never blocked.

const SECTION_LABEL = {
  fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em',
  textTransform: 'uppercase', color: 'var(--text-muted)',
};

function Field({ label, value, onChange, placeholder, required, hint, invalid }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <label style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
        {label}
        {required && <span style={{ color: 'var(--brand-light)' }}>*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        style={{
          height: 34, padding: '0 10px', borderRadius: 6, outline: 'none',
          border: `1px solid ${invalid ? 'var(--danger-border)' : 'var(--border-default)'}`,
          background: 'var(--surface-default)', color: 'var(--text-primary)',
          fontFamily: 'inherit', fontSize: 14,
        }} />
      {hint && <span style={{ fontSize: 12, color: invalid ? 'var(--danger-text)' : 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function EmployeeForm({ employee, existingNames = [], heldCount = 0, onSave, onCancel }) {
  const editing = !!employee;
  const [f, setF] = useState(() => ({
    name: (editing && employee.name) || '',
    role: (editing && employee.role) || '',
    team: (editing && employee.team) || '',
    base: (editing && employee.base) || '',
    phone: (editing && employee.phone) || '',
    email: (editing && employee.email) || '',
  }));
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const name = f.name.trim();

  // Two people with the same name make the assignment list ambiguous — you
  // cannot tell whose devices are whose — so it is worth catching here.
  const duplicate = !!name && existingNames.some(
    n => n.toLowerCase() === name.toLowerCase() && !(editing && n === employee.name)
  );

  const canSave = !!name && !duplicate;

  const submit = () => {
    if (!canSave) return;
    onSave({
      name,
      role: f.role.trim(),
      team: f.team.trim(),
      base: f.base.trim(),
      phone: f.phone.trim(),
      email: f.email.trim(),
    });
  };

  const gateNote = duplicate
    ? 'Someone with that name is already on the list.'
    : !name
      ? 'Needs a full name.'
      : editing
        ? 'Changes apply wherever this person appears, including devices already signed out to them.'
        : 'They can hold devices as soon as they are added.';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-default)', color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: 'pointer', marginTop: 2 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.3px', color: 'var(--text-primary)' }}>
            {editing ? 'Edit employee' : 'Add employee'}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>
            {editing
              ? 'Changes apply wherever this person appears, including devices already signed out to them.'
              : 'Someone who can hold devices. Adding them here does not create a Bench login.'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: '1 1 480px', minWidth: 0, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '12px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
            <span style={SECTION_LABEL}>Person</span>
            <span style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>Who they are</span>
          </div>
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, borderBottom: '1px solid var(--border-default)' }}>
            <Field
              label="Full name" required value={f.name} onChange={v => set('name', v)}
              placeholder="Simon Prentice"
              invalid={!name || duplicate}
              hint={duplicate ? 'Already on the list' : (!name ? 'Required' : '')} />
            <Field label="Role" value={f.role} onChange={v => set('role', v)} placeholder="Field engineer" hint="Optional" />
            <Field label="Team" value={f.team} onChange={v => set('team', v)} placeholder="Field ops" hint="Optional" />
            <Field label="Based at" value={f.base} onChange={v => set('base', v)} placeholder="Reigate" hint="Optional" />
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '12px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
            <span style={SECTION_LABEL}>Contact</span>
            <span style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>How to reach them when a device is due back</span>
          </div>
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, borderBottom: '1px solid var(--border-default)' }}>
            <Field label="Phone" value={f.phone} onChange={v => set('phone', v)} placeholder="07700 900000" hint="Optional" />
            <Field label="Email" value={f.email} onChange={v => set('email', v)} placeholder="name@onwave.com" hint="Optional" />
          </div>

          <div style={{ padding: '12px 16px', background: 'var(--surface-inset)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={submit}
              disabled={!canSave}
              className="btn btn-primary"
              style={{ height: 32, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}>
              {editing ? 'Save changes' : 'Add employee'}
            </button>
            <button type="button" onClick={onCancel} className="btn btn-ghost" style={{ height: 32 }}>Cancel</button>
            <span style={{ flex: 1, fontSize: 12, color: canSave ? 'var(--text-muted)' : 'var(--danger-text)' }}>{gateNote}</span>
          </div>
        </div>

        <div style={{ flex: '0 1 320px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: '1px solid var(--border-default)' }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
                {editing ? 'This person' : 'The person this adds'}
              </span>
            </div>
            {[
              { k: 'Name', v: name || 'Not named', set: !!name },
              { k: 'Role', v: f.role.trim() || 'Not set', set: !!f.role.trim() },
              { k: 'Team', v: f.team.trim() || 'Not set', set: !!f.team.trim() },
              { k: 'Based at', v: f.base.trim() || 'Not set', set: !!f.base.trim() },
              { k: 'Phone', v: f.phone.trim() || 'Not set', set: !!f.phone.trim() },
              { k: 'Holding', v: editing ? `${heldCount} device${heldCount === 1 ? '' : 's'}` : 'Nothing yet', set: editing && heldCount > 0 },
            ].map(p => (
              <div key={p.k} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{p.k}</span>
                <span style={{ fontSize: 14, fontWeight: 500, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: p.set ? 'var(--text-primary)' : 'var(--text-muted)' }}>{p.v}</span>
              </div>
            ))}
            <div style={{ padding: '12px 16px', background: 'var(--surface-inset)', fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
              {editing
                ? 'Saving updates this person everywhere they appear, including any devices already signed out to them.'
                : 'An employee is a named holder of devices. Adding them here does not create a Bench login or grant any access.'}
            </div>
          </div>

          <div className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              Required<span style={{ color: 'var(--brand-light)' }}>*</span>
            </div>
            <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
              A full name is all that is needed. Role, team and contact details are what make the assignments list readable when a device is overdue, so they are worth filling in.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeForm;
